import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { FeatureFlagsService } from '@ideals/core/feature-flags';
import { createHttpParams } from '@ideals/utils/create-http-params';
import { getUserDisplayName } from '@ideals/utils/get-user-display-name';
import { sortItems } from '@ideals/utils/sort-items';

import {
  Country,
  SubscriptionPlanId,
  UserAccount,
  UserAccounts,
} from '@vdr/data/models';

import { ContractType } from '../../../billing/models';
import { ProjectServer } from '../../../project/models';
import {
  BusinessDevelopmentManager,
  ContractExpirationInfo,
  CorporateAccount,
  CorporateAccountPlanRequestPlaces,
  CorporateAccountsFilters,
  CorporateAccountsUsage,
  DetailedAdministrator,
  DetailedCorporateAccount,
  DetailedRegularCorporateAccount,
  ListCorporateAccount,
  Manager,
  RetentionSettings,
  Status,
  StatusChangeReason,
  StatusId,
  SubscriptionPlan,
  User,
} from '../../models';

interface CorporateAccountDto extends Omit<CorporateAccount, 'creationDate'> {
  readonly creationDate: string;
}

interface CreateAccountDto {
  readonly accountManagerId: number;
  readonly accountName: string;
  readonly accountUsers: User[];
  readonly administrators: CorporateAccountAdministratorDto[] | null;
  readonly contractNumber: string | null;
  readonly countryId: number;
  readonly creationDate: string;
  readonly forbidCreateTrialAccount: boolean;
  readonly isArchivingEnabled: boolean;
  readonly publicApiEnabled: boolean;
  readonly subscriptionPlanId: SubscriptionPlanId;
  readonly trialAutoClosureEnabled: boolean;
  readonly trialExpirationDate: string | null;
}

interface BillingSubscriptionAccessDto {
  readonly hasActiveSubscription: boolean;
  readonly hasSubscriptionToReview: boolean;
  readonly showSubscriptionBuilder: boolean;
  readonly subscriptionId?: string;
  readonly subscriptionType?: ContractType;
}

interface ContractExpirationInfoDto {
  readonly accountManagerEmail: string | null;
  readonly expiryDate: string | null;
}

export interface DetailedCorporateAccountDto extends CorporateAccountDto, Omit<
DetailedCorporateAccount,
'administrators' |
'archivingEnabled' |
'closureDate' |
'creationDate' |
'retentionEndDate' |
'trialExpirationDate'
> {
  readonly administrators: DetailedCorporateAccountAdministratorDto[];
  readonly closureDate: string | null;
  readonly isArchivingEnabled: boolean;
  readonly retentionEndDate: string | null;
  readonly trialExpirationDate: string | null;
}

interface ListCorporateAccountDto {
  readonly accountManager?: Manager;
  readonly accountManagerEmail: string;
  readonly accountName: string;
  readonly administrators: string[];
  readonly clientActivationDate: string | null;
  readonly closureDate: string;
  readonly contractNumber: string;
  readonly country: Country | null;
  readonly creationDate: string;
  readonly humanReadableId: string;
  readonly id: string;
  readonly numberOfActiveRooms: number;
  readonly numberOfRooms: number;
  readonly status: Status;
  readonly subscriptionPlan: SubscriptionPlan;
  readonly trialExpirationDate: string;
  readonly uniqueRoomAdministratorsCount: number;
  readonly uniqueRoomUsersCount: number;
}

export interface DetailedRegularCorporateAccountDto {
  readonly accountManager: Manager;
  readonly accountName: string;
  readonly businessDevelopmentManager: BusinessDevelopmentManager | null;
  readonly closureDate: string | null;
  readonly creationDate: string;
  readonly humanReadableId: string;
  readonly retentionSettings: RetentionSettings;
  readonly status: StatusId;
  readonly trialExpirationDate: string | null;
}

interface EditAccountDto extends CreateAccountDto {
  readonly businessDevelopmentManagerId: number | null;
  readonly clientRequestUrl: string | null;
  readonly deactivateExternalUsers: boolean | null;
  readonly integrationsApiEnabled: boolean;
  readonly mcpApiEnabled: boolean;
  readonly retentionSettings: RetentionSettings | null;
  readonly showBillingInformation: boolean;
  readonly showOutstandingBalance: boolean;
  readonly status: StatusId;
  readonly statusChangeReason: StatusChangeReason | null;
  readonly trialExpirationDate: string | null;
  readonly trialProlongationReason: string | null;
}

interface CorporateAccountAdministratorDto {
  readonly canCreateRooms: boolean;
  readonly canManageAdministrators: boolean;
  readonly email: string;
}

interface UserAccountDto {
  readonly accountName: string;
  readonly allowedHosts: ProjectServer[];
  readonly defaultAdmins: string[];
  readonly id: string;
  readonly subscriptionPlanId: SubscriptionPlanId;
}

export interface CorporateAccountMatching {
  readonly humanReadableId: string;
  readonly id: string;
  readonly name: string;
  readonly status: StatusId;
  readonly subscriptionPlan: SubscriptionPlan;
}

type DetailedCorporateAccountAdministratorDto = Omit<DetailedAdministrator, 'name'> & {
  readonly firstName: string;
  readonly lastName: string;
};

type DetailedCorporateAccountWithExternalUsers = DetailedCorporateAccount & {
  readonly clientRequestUrl: string | null;
  readonly deactivateExternalUsers?: boolean;
};

function mapCorporateAccount({ creationDate, ...rest }: CorporateAccountDto): CorporateAccount {
  return { ...rest, creationDate: new Date(creationDate) };
}

function mapCorporateAccountAdministrator(
  { permissions, ...rest }: DetailedAdministrator,
): CorporateAccountAdministratorDto {
  return { ...rest, ...permissions };
}

function mapCorporateAccountAdministratorDto(
  dto: DetailedCorporateAccountAdministratorDto,
): DetailedAdministrator {
  return {
    ...dto,
    name: getUserDisplayName(dto),
    lastLogin: dto.lastLogin ? new Date(dto.lastLogin) : null,
  };
}

function mapCorporateAccountAdministrators(
  admins: DetailedCorporateAccountAdministratorDto[],
): DetailedAdministrator[] {
  return admins?.map((admin) => mapCorporateAccountAdministratorDto(admin));
}

export function mapCreateAccount(account: DetailedCorporateAccount): CreateAccountDto {
  return {
    accountManagerId: account.accountManager.id,
    accountName: account.accountName,
    accountUsers: account.accountUsers ?? [],
    administrators: account.administrators ? account.administrators.map((admin) => mapCorporateAccountAdministrator(admin)) : null,
    contractNumber: account.contractNumber,
    countryId: account.country!.id,
    creationDate: account.creationDate.toISOString(),
    forbidCreateTrialAccount: account.forbidCreateTrialAccount,
    isArchivingEnabled: account.archivingEnabled,
    publicApiEnabled: account.publicApiEnabled,
    subscriptionPlanId: account.subscriptionPlan.id,
    trialAutoClosureEnabled: account.trialAutoClosureEnabled,
    trialExpirationDate: account.trialExpirationDate ? account.trialExpirationDate.toISOString() : null,
  };
}

export function mapDetailedCorporateAccount({
  administrators,
  closureDate,
  isArchivingEnabled,
  preparationEndDate,
  preparationStartDate,
  retentionEndDate,
  trialExpirationDate,
  ...rest
}: DetailedCorporateAccountDto): DetailedCorporateAccount {
  return {
    ...rest,
    ...mapCorporateAccount(rest),
    administrators: mapCorporateAccountAdministrators(administrators),
    archivingEnabled: isArchivingEnabled,
    closureDate: closureDate ? new Date(closureDate) : null,
    preparationEndDate: preparationEndDate ? new Date(preparationEndDate) : undefined,
    preparationStartDate: preparationStartDate ? new Date(preparationStartDate) : undefined,
    retentionEndDate: retentionEndDate ? new Date(retentionEndDate) : null,
    trialExpirationDate: trialExpirationDate ? new Date(trialExpirationDate) : null,
  };
}

export function mapDetailedRegularCorporateAccount(
  regularAccountDetails: DetailedRegularCorporateAccountDto,
): DetailedRegularCorporateAccount {
  return {
    ...regularAccountDetails,
    closureDate: regularAccountDetails.closureDate ? new Date(regularAccountDetails.closureDate) : null,
    creationDate: new Date(regularAccountDetails.creationDate),
    retentionSettings: regularAccountDetails.retentionSettings,
    trialExpirationDate: regularAccountDetails.trialExpirationDate ? new Date(regularAccountDetails.trialExpirationDate) : null,
  };
}

export function mapEditAccount(account: DetailedCorporateAccountWithExternalUsers): EditAccountDto {
  return {
    ...mapCreateAccount(account),
    clientRequestUrl: account.clientRequestUrl ?? null,
    deactivateExternalUsers: account.deactivateExternalUsers ?? null,
    businessDevelopmentManagerId: account.businessDevelopmentManager?.id ?? null,
    integrationsApiEnabled: account.integrationsApiEnabled,
    mcpApiEnabled: account.mcpApiEnabled,
    retentionSettings: account.retentionSettings?.period == null ? null : account.retentionSettings,
    showBillingInformation: account.showBillingInformation,
    showOutstandingBalance: account.showOutstandingBalance,
    status: account.status,
    statusChangeReason: account.statusChangeReason,
    trialProlongationReason: account.trialProlongationReason,
  };
}

function mapUserAccount(dto: UserAccountDto): UserAccount {
  return {
    allowedHosts: dto.allowedHosts.length ? dto.allowedHosts : undefined,
    defaultAdmins: dto.defaultAdmins ?? [],
    id: dto.id,
    name: dto.accountName,
    subscriptionPlanId: dto.subscriptionPlanId,
  };
}

@Service()
export class CorporateAccountService {
  readonly #featureFlagsService = inject(FeatureFlagsService);
  readonly #httpClient = inject(HttpClient);

  checkOutstandingBalance(): Observable<boolean> {
    return this.#httpClient.get<boolean>('/api/ca/account/outstanding-balance')
      .pipe(catchError(() => of(false)));
  }

  createAccount(account: DetailedCorporateAccount, checkSsoUsers: boolean): Observable<string> {
    const params = new HttpParams().set('checkSsoUsers', checkSsoUsers);

    return this.#httpClient.post<string>('/api/ca/fca/account', mapCreateAccount(account), { params });
  }

  editAccount(account: DetailedCorporateAccountWithExternalUsers): Observable<DetailedCorporateAccount> {
    return this.#httpClient.put<DetailedCorporateAccount>(`/api/ca/fca/${account.id}/account`, mapEditAccount(account));
  }

  loadAccount(id: string): Observable<CorporateAccount> {
    return forkJoin([
      this.#httpClient.get<CorporateAccountDto>(`/api/ca/fca/${id}/account`),
      this.#featureFlagsService.isEnabled$('fvdr-contract-builder-customer-side')
        .pipe(switchMap((enabled) => enabled
          ? this.#httpClient.get<BillingSubscriptionAccessDto>(`/api/public/payment-service/subscriptions/ca/${id}/access-control-info`)
          : of(undefined))),
    ])
      .pipe(
        map(([account, billingSubscriptionAccess]) => {
          const {
            hasActiveSubscription,
            hasSubscriptionToReview,
            showSubscriptionBuilder,
            subscriptionId,
            subscriptionType,
          } = billingSubscriptionAccess ?? {};

          return mapCorporateAccount(
            {
              ...account,
              hasActiveSubscription,
              hasSubscriptionToReview,
              showBillingSubscriptionTab: !!hasActiveSubscription || !!hasSubscriptionToReview || !!showSubscriptionBuilder,
              subscriptionId,
              subscriptionType,
            },
          );
        }),
      );
  }

  loadAccountsFromAuth(filters: CorporateAccountsFilters): Observable<ListCorporateAccount[]> {
    const filtrationParams = this.#getFiltrationParams(filters, false);

    return this.#httpClient.get<ListCorporateAccountDto[]>(
      '/api/authorization-service/accounts/search',
      {
        params: {
          ...filtrationParams,
        },
      },
    ).pipe(map((accounts) => this.#mapCorporateAccounts(accounts)));
  }

  loadAccountsMatching(query: string, accountId: string): Observable<CorporateAccountMatching[]> {
    return this.#httpClient.get<CorporateAccountMatching[]>(
      `/api/ca/accounts/${accountId}/search/matching`,
      { params: { query } },
    );
  }

  loadAccountsUsage(accountsIds: string[]): Observable<CorporateAccountsUsage[]> {
    return this.#httpClient.post<CorporateAccountsUsage[]>(
      '/api/usage/accounts/search',
      accountsIds,
    );
  }

  loadAdministratedAccounts(requestParams: {
    readonly onlyFullAdmins?: boolean;
    readonly statuses: StatusId[];
    readonly verified?: boolean;
  }): Observable<UserAccounts> {
    const params = createHttpParams(requestParams);

    return this.#httpClient.get<{
      administrated: UserAccountDto[];
      forbidCreateTrialAccount: boolean;
      participated: UserAccountDto[];
    }>('/api/ca/accounts', { params }).pipe(
      map((accountDtos) => {
        return {
          administrated: sortItems(
            accountDtos.administrated.map((dto) => mapUserAccount(dto)),
            [{ field: 'name', order: 1 }],
          ),
          participated: accountDtos.participated.map((dto) => mapUserAccount(dto)),
          forbidCreateTrialAccount: accountDtos.forbidCreateTrialAccount,
        };
      }),
    );
  }

  loadContractExpirationInfo(): Observable<ContractExpirationInfo | undefined> {
    return this.#httpClient.get<ContractExpirationInfoDto>('/api/ca/account/expiry-stage')
      .pipe(
        map(({ accountManagerEmail, expiryDate }) => accountManagerEmail && expiryDate
          ? { accountManagerEmail, expiryDate: new Date(expiryDate) }
          : undefined),
        catchError(() => of(undefined)),
      );
  }

  loadExternalUsersCount(accountId: string): Observable<number> {
    return this.#httpClient.get<number>(`/api/ca/fca/${accountId}/external-users`);
  }

  loadMaxTrialExpirationDays(): Observable<number> {
    return this.#httpClient.get<{ content: { maxTrialExpirationDays: number; }; }>('/api/ca/config/validation')
      .pipe(
        map(({ content: { maxTrialExpirationDays } }) => maxTrialExpirationDays),
      );
  }

  loadPermissions(accountId?: string): Observable<string[]> {
    const params = createHttpParams(accountId ? { accountId } : {});

    return this.#httpClient.get<string[]>('/api/ca/accounts/permissions', { params });
  }

  requestPlanUpgrade(accountId: string, planRequestPlace: CorporateAccountPlanRequestPlaces, projectId?: string): Observable<void> {
    const params = createHttpParams({ planRequestPlace, projectId });

    return this.#httpClient.get<void>(`/api/ca/fca/${accountId}/request-plan-upgrade`, { params });
  }

  #getFiltrationParams(filters: CorporateAccountsFilters, withPrefix = true): object {
    const entries = Object.entries(filters)
      .filter(([, value]) => !!value)
      .map(([key, value]: [string, unknown]): [string, unknown] => {
        const entryValue = value instanceof Date ? value.toISOString() : value;

        return [`${withPrefix ? 'accountRequest.' : ''}${key}`, entryValue];
      });

    return Object.fromEntries(entries) as object;
  }

  #mapCorporateAccounts(accounts: ListCorporateAccountDto[]): ListCorporateAccount[] {
    return accounts.map((account) => ({
      ...account,
      accountManager: account.accountManager?.name ?? account.accountManagerEmail,
      activeProjectsCount: account.numberOfActiveRooms,
      clientActivationDate: account.clientActivationDate ? new Date(account.clientActivationDate) : null,
      creationDate: new Date(account.creationDate),
      closureDate: account.closureDate ? new Date(account.closureDate) : null,
      projectsCount: account.numberOfRooms,
      trialExpirationDate: account.trialExpirationDate ? new Date(account.trialExpirationDate) : null,
      projectsAdmins: account.uniqueRoomAdministratorsCount ?? 0,
      projectsUsers: account.uniqueRoomUsersCount ?? 0,
      usedStorage: 0,
    }));
  }
}
